import { serviceHelper } from '../../../../config/service-helper';
import { PhoneHelper } from '../../../../utils/phoneHelper/PhoneHelper';
import { fetchJson } from './helpers';

export const delay = ms => new Promise(resolve => setTimeout(() => resolve(true), ms));

export default class CustomerHelper {
  constructor() {
    this.fetchCustomerInfo = this.fetchCustomerInfo.bind(this);
    this.getCustomerId = this.getCustomerId.bind(this);
    this.customerLoginVerify = this.customerLoginVerify.bind(this);
    this.storeVerifiedUser = this.storeVerifiedUser.bind(this);
    this.deleteCustomerVerifiedMobile = this.deleteCustomerVerifiedMobile.bind(this);
  }

  async fetchCustomerInfo(skipCustomerIdRetrys = false) {
    let fetchAttempts = 0;
    let customerData = null;

    if (skipCustomerIdRetrys) {
      const result = await fetchJson(`${location.origin}/account?view=customerjson`);
      customerData = result;
    } else {
      while (!customerData?.customerId && fetchAttempts < 10) {
        await delay(1000);
        const result = await fetchJson(`${location.origin}/account?view=customerjson`);
        customerData = result;
        fetchAttempts += 1;
      }
    }

    if (customerData.shopifyCustomerId) {
      window.Jbhifi = window.Jbhifi || {};
      window.Jbhifi.customerDetails = window.Jbhifi.customerDetails || {};
      window.Jbhifi.customerDetails.isLoggedIn = true;
      window.Jbhifi.customerEmail = customerData.customerEmail;
      window.Jbhifi.shopifyCustomerId = customerData.shopifyCustomerId;
      window.Jbhifi.hashedCustomerEmail = customerData.hashedCustomerEmail;
      window.Jbhifi.customerId = customerData.customerId;
      window.Shopify = window.Shopify || {};
      window.Shopify.Customer = window.Shopify.Customer || {};
      window.Shopify.Customer.shopifyCustomerId = customerData.shopifyCustomerId;
      window.Shopify.Customer.customerEmail = customerData.customerEmail;
      window.Shopify.Customer.customerId = customerData.customerId;
      window.Shopify.Customer.apiQueryParams = customerData.apiQueryParams;
      window.Shopify.Customer.customerHash = customerData.customerHash;
    }

    return;
  }

  async getCustomerId(existingCustomerId) {
    if (existingCustomerId) return existingCustomerId;
    await fetchJson(serviceHelper.customerEnableDigitalReceipt, {
      body: JSON.stringify({
        customerId: window.Jbhifi.customerId,
        shopifyCustomerId: `${window.Jbhifi.shopifyCustomerId}`,
      }),
      method: 'post',
    });
    await this.fetchCustomerInfo();
    return window.Jbhifi.customerId;
  }

  async customerLoginVerify(data) {
    await fetchJson(serviceHelper.customerLoginVerify, { body: JSON.stringify(data), method: 'POST' });
  }

  storeVerifiedUser(email) {
    const hashedEmail =
      email?.toLowerCase() === window.Jbhifi?.customerEmail?.toLowerCase() ? window.Jbhifi?.hashedCustomerEmail : '';

    if (hashedEmail) {
      window.localStorage.setItem(`${hashedEmail}-verified`, 'true');
    }
  }

  async sendCustomerMobileVerification(shopifyCustomerId, receiptId) {
    const response = await fetchJson(
      `${serviceHelper.customerAPIURL}/customers/${shopifyCustomerId}/receipts/${receiptId}?${window?.Shopify?.Customer?.apiQueryParams}`,
      {
        method: 'POST',
      }
    );
    return this.generateCustomerResponse(response);
  }

  async getCustomerData(shopifyCustomerId) {
    const response = await fetchJson(
      `${serviceHelper.customerAPIURL}/customers/${shopifyCustomerId}?${window?.Shopify?.Customer?.apiQueryParams}`
    );
    return this.generateCustomerResponse(response);
  }

  async setCustomerMobile(shopifyCustomerId, phoneNumber, force = false) {
    const getFailResponse = errorCode => this.generateCustomerResponse({ success: false, error: { code: errorCode } });

    const countryCode = window.Jbhifi?.storeLocation || 'AU';
    const { isValid, e164Format } = await PhoneHelper.validateWithApi({
      phoneNumber,
      countryCode,
      localNumbersOnly: true,
    });
    if (!isValid) {
      return getFailResponse('BadArgument');
    }

    const response = await fetchJson(
      `${serviceHelper.customerAPIURL}/customers/${shopifyCustomerId}/mobile?${window?.Shopify?.Customer?.apiQueryParams}`,
      {
        method: 'POST',
        body: JSON.stringify({
          Mobile: e164Format,
          SkipMobileOwnershipCheck: force,
        }),
      }
    );
    return this.generateCustomerResponse(response);
  }

  async deleteCustomerVerifiedMobile(shopifyCustomerId) {
    const response = await fetchJson(
      `${serviceHelper.customerAPIURL}/customers/${shopifyCustomerId}/mobile?${window?.Shopify?.Customer?.apiQueryParams}`,
      { method: 'DELETE' }
    );

    return this.generateCustomerResponse(response);
  }

  async verifyCustomerMobile(shopifyCustomerId, verificationCode) {
    const response = await fetchJson(
      `${serviceHelper.customerAPIURL}/customers/${shopifyCustomerId}/mobile/latest/verification/${verificationCode}?${window?.Shopify?.Customer?.apiQueryParams}`,
      { method: 'POST' }
    );
    return this.generateCustomerResponse(response);
  }

  async setCustomerPreferences(shopifyCustomerId, preferences = {}) {
    const response = await fetchJson(
      `${serviceHelper.customerAPIURL}/customers/${shopifyCustomerId}/preferences?${window?.Shopify?.Customer?.apiQueryParams}`,
      {
        method: 'PUT',
        body: JSON.stringify({ preferences }),
      }
    );
    return this.generateCustomerResponse(response);
  }

  async getCustomerReceipts(shopifyCustomerId, signal = null) {
    const response = await fetchJson(
      `${serviceHelper.customerAPIURL}/customers/${shopifyCustomerId}/receipts?${window?.Shopify?.Customer?.apiQueryParams}`,
      {
        method: 'GET',
      },
      undefined,
      undefined,
      signal
    );
    return response;
  }

  async getCustomerReceipt(shopifyCustomerId, orderId, signal = null) {
    const response = await fetchJson(
      `${serviceHelper.customerAPIURL}/customers/${shopifyCustomerId}/receipts/${orderId}?${window?.Shopify?.Customer?.apiQueryParams}`,
      {
        method: 'GET',
      },
      undefined,
      undefined,
      signal
    );
    return response;
  }

  async getCustomerReceiptPDF(shopifyCustomerId, orderId) {
    const response = await fetchJson(
      `${serviceHelper.customerAPIURL}/customers/${shopifyCustomerId}/receipts/${orderId}/download?${window?.Shopify?.Customer?.apiQueryParams}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/pdf',
        },
      },
      undefined,
      'text'
    );
    return response;
  }

  async sendCustomerReceiptEmail(shopifyCustomerId, orderId, emailAddress) {
    const response = await fetchJson(
      `${serviceHelper.customerAPIURL}/customers/${shopifyCustomerId}/receipts/${orderId}/email?${window?.Shopify?.Customer?.apiQueryParams}`,
      {
        method: 'POST',
        body: JSON.stringify({
          EmailAddress: emailAddress,
        }),
      }
    );
    return response;
  }

  formatMobileNumber(number) {
    if (!number) return '';
    number = number.replace(/ /g, '');
    if (window.Jbhifi?.storeLocation === 'AU') number = number.replace('+61', '0');
    if (window.Jbhifi?.storeLocation === 'NZ') number = number.replace('+64', '0');
    if (number[0] !== '0') number = `0${number}`;
    return `${number.substr(0, 4)} ${number.substr(4, 3)} ${number.substr(7)}`;
  }

  generateCustomerResponse(rawResponse) {
    const response = {
      isSuccess: false,
      isAmbiguous: false,
      isTooManyAttempts: false,
      isBadArgument: false,
      isNoMatch: false,
      isAlreadyVerified: false,
      raw: {},
    };

    response.isSuccess = rawResponse.success;
    response.isAmbiguous = rawResponse.error?.code == 'AmbiguousPhone';
    response.isTooManyAttempts = rawResponse.error?.code == 'TooManyAttempts';
    response.isBadArgument = rawResponse.error?.code == 'BadArgument';
    response.isNoMatch = rawResponse.error?.code == 'VerificationNoMatch';
    response.isCustomerNoMatch = rawResponse.error?.code == 'NoMatch';
    response.isAlreadyVerified = rawResponse.error?.code == 'AlreadyVerifiedPhone';
    response.raw = rawResponse;

    return response;
  }
}
